import lib from '@/lib';
import { createI18n } from "vue-i18n";
import ru from "@/locales/ru.json";
import en from "@/locales/en.json";
import axios from "axios";

import AuthModel from "@/models/AuthModel.js";

export default class AppModel {
	constructor() {
		this.hasInit = false;
		
		this.langs = ["ru", "en"];
		this.langsName = {
			ru: "Русский",
			en: "English",
		};
		this.title = "";
		this.i18n = createI18n({
			legacy: false,
			locale: "ru",
			messages: { ru, en },
		});

		this.auth = new AuthModel();

		this.enableRegister = false;
		this.contentMainpage = "";
		this.sidebar2 = [];

		this.users_count = 0;
		this.projects_count = 0;
	}

	/**
	 * Set page title
	 */
	setPageTitle(title) {
		this.title = title;
		document.title = title;
	}

	/**
	 * Set app lang
	 */
	setLang(lang = "ru") {
		this.i18n.global.locale = lang;
		//axios.defaults.headers.common['Accept-Language'] = lang;
		document.querySelector("html").setAttribute("lang", lang);
		return lang;
	}

	/**
	 * Returns lang
	 */
	getLang() {
		//return this.i18n.locale;
		return this.i18n.global.locale;
	}

	pageReload() {
		document.location.reload();
	}

	/**
	 * Инициализация данных
	 */
	async doInitData() {
		if (this.hasInit) return true;

		const retSet = await this.doGetSettings();
		if (retSet) {
			this.enableRegister = !!retSet.enable_register;
			this.contentMainpage = retSet.content_mainpage;
			this.hasInit = true;
			return true;
		}
		
		return false;
	}
	
	async refreshCounts() {
		// Проверка наличия токена
		if (!this.auth.accessToken) return;

		try {
			const isAdmin = this.auth.isAdmin();

			// Выполняем запросы
			const [ret1, ret2] = await Promise.all([
				isAdmin
					? this.doGetUsersCount()
					: Promise.resolve({ success: true, count: 0 }),
				this.doGetProjectsCount(),
			]);

			// Обновляем реактивные свойства модели
			if (ret1.success) this.users_count = ret1.count;
			if (ret2.success) this.projects_count = ret2.count;
		} catch (e) {
			console.warn("Ошибка обновления счетчиков:", e.message);
		}
	}
	
	async refreshSidebarLinks() {
		try {
			const data = await this.doGetSidebarLinks()
			const list = Array.isArray(data) ? data : (data.list || []);
			this.sidebar2 = list.map(item => ({
				id: item.id,
				text: item.text,
				link: item.link,
				isTargetBlank: !!item.is_target_blank,
			}));
		} catch (e) {
			console.error("Ошибка загрузки ссылок меню:", e);
			this.sidebar2 = [];
		}
	}

	/**
	 * Получение списка пользователей
	 */
	async doGetUsers(params = {}) {
		return await lib.request("/api/users");
	}

	/**
	 * Получение количества пользователей
	 */
	async doGetUsersCount() {
		return await lib.request("/api/users/count");
	}

	/**
	 * Добавление пользователя
	 */
	async doAddUser(formData) {
		return await lib.request("/api/users", 'POST', formData);
	}

	/**
	 * Получение данных о пользователе по его id
	 */
	async doGetUser(user_id) {
		return await lib.request("/api/users/" + user_id);
	}

	/**
	 * Изменение данных о пользователе по его id
	 */
	async doEditUser(user_id, formData) {
		return await lib.request("/api/users/" + user_id, 'POST', formData);
	}

	/**
	 * Удаление пользователя по его id
	 */
	async doDeleteUser(user_id) {
		return await lib.request("/api/users/" + user_id, 'DELETE');
	}

	/**
	 * Получение списка групп пользователей
	 */
	async doGetUserGroups(params = {}) {
		return await lib.request("/api/users/groups");
	}

	/**
	 * Получение количества групп пользователей
	 */
	async doGetUserGroupsCount() {
		return await lib.request("/api/users/groups/count");
	}

	/**
	 * Добавление группы пользователей
	 */
	async doAddUserGroup(params = {}) {
		return await lib.request("/api/users/groups", 'POST', params);
	}

	/**
	 * Получение данных о группе пользователей по ее id
	 */
	async doGetUserGroup(group_id) {
		return await lib.request("/api/users/groups/" + group_id);
	}

	/**
	 * Изменение данных о группе пользователей по ее id
	 */
	async doEditUserGroup(group_id, params = {}) {
		return await lib.request("/api/users/groups/" + group_id, 'POST', params);
	}

	/**
	 * Удаление группы пользователей по ее id
	 */
	async doDeleteUserGroup(group_id) {
		return await lib.request("/api/users/groups/" + group_id, 'DELETE');
	}

	/**
	 * Получение списка проектов
	 */
	async doGetProjects(params = {}) {
		return await lib.request("/api/projects");
	}

	/**
	 * Получение количества проектов
	 */
	async doGetProjectsCount() {
		return await lib.request("/api/projects/count");
	}

	/**
	 * Добавление проекта
	 */
	async doAddProject(formData) {
		return await lib.request("/api/projects", 'POST', formData);
	}

	/**
	 * Получение данных о проекте по его id
	 */
	async doGetProject(project_id) {
		return await lib.request("/api/projects/" + project_id);
	}

	/**
	 * Изменение данных о проекте по его id
	 */
	async doEditProject(project_id, formData) {
		return await lib.request("/api/projects/" + project_id, 'POST', formData);
	}

	/**
	 * Удаление проекта по его id
	 */
	async doDeleteProject(project_id) {
		return await lib.request("/api/projects/" + project_id, 'DELETE');
	}

	/**
	 * Обновление профиля авторизованного пользователя
	 */
	async doUpdateProfile(formData) {
		return await lib.request("/api/profile", 'POST', formData);
	}

	// Геттеры для инициализации
	async doGetSettings() {
		return await lib.request("/api/settings");
	}
	
	async doSaveSettings(params = {}) {
		return await lib.request("/api/settings", 'POST', params);
	}

	async doGetSidebarLinks() {
		return await lib.request("/api/links");
	}
	
	async doSaveSidebarLinks(params = {}) {
		return await lib.request("/api/links", 'POST', params);
	}
}
